<template>
  <tr-text-field
    ref="autocomplete"
    v-model="modelValue"
    :label="label"
    :name="name"
    :warning="warning"
    :placeholder="placeholder"
    :hide-message-on-error="hideMessageOnError"
    :error="error"
    :no-margin="noMargin"
    :required="required"
    :mask-format="maskFormat"
    :type="type || 'text'"
    :classes="classes"
  />
</template>

<script>
import autocomplete from 'autocomplete.js';
import TrTextField from './TrTextField.vue';
import createSuggestionTemplate from '../services/autocomplete/createSuggestionTemplate';
import createComponentTemplate from '../services/autocomplete/createComponentTemplate';
import buildInstanceOptions from '../services/autocomplete/buildInstanceOptions';
import searchData from '../services/autocomplete/searchData';
import jQuery from 'jquery';

const component = {
  name: 'tr-autocomplete',
  props: ['url', 'display', 'autocomplete',
    'openOnFocus', 'emptyResultMessage', 'onSelect',
    'getData', 'searchFilter', 'suggestionComponent',
    'label', 'name', 'required', 'value', 'placeholder',
    'classes', 'type', 'hint', 'maskFormat', 'warning',
    'hideMessageOnError', 'error', 'addNewItemLabel',
    'addNewItemCallback', 'addNewItemOnBlur', 'noMargin',
    'minimumCharsToSearch', 'addButtonTop', 'addButtonColor',
    'suggestionsHeaderComponent', 'suggestionsFooterComponent'],
  data: () => ({
    instance: null,
    modelValue: '',
    hasSelected: false
  }),
  components: {
    TrTextField
  },
  mounted() {
    if (!this.suggestionComponent) {
      throw new Error('No suggestion component was supplied to autocomplete');
    }
    this.instance = createInstance.call(this);
    this.instance.on('autocomplete:selected', this._onSelect);
    this.instance.on('autocomplete:updated', this._onUpdated);
    this.instance.on('autocomplete:cursorremoved', this._onCursorRemoved);
    this.instance[0].addEventListener('keypress', this._onKeypress);
    this.instance[0].addEventListener('blur', this._onBlur);
    this.instance[0].addEventListener('keydown', this._onKeyDown);
    this.syncAutocompleteValWithInputVal(this.value);
  },
  watch: {
    value(newVal) {
      this.syncAutocompleteValWithInputVal(newVal);
    }
  },
  computed: {
    addButtonColorValue() {
      if (!this.addButtonColor) {
        return 'blue';
      }
      return this.addButtonColor;
    }
  },
  methods: {
    _onSelect(ev, suggestion) {
      this.hasSelected = true;
      this.onSelect(suggestion);
    },
    _onUpdated() {
      if (this._hasAddNewItemBtn()) {
        const $addButton = this.instance[0].parentElement.querySelector('.add-button');
        if ($addButton) {
          $addButton.addEventListener('click', this._onAddButtonClick);
        }
      }
    },
    focus() {
      this.$refs.autocomplete.focus();
    },
    _onKeypress(event) {
      const hasSuggestions = this.instance[0].querySelector('.autocomplete-dropdown-suggestion');
      const ENTER_KEY = 13;

      if (event.keyCode === ENTER_KEY) {
        if (!hasSuggestions) {
          this._onAddButtonClick();
        }
        event.preventDefault();
      }
    },
    _onBlur() {
      if (!this.hasSelected &&
        this.addNewItemOnBlur &&
        this.instance.val()) {
        this.addNewItemCallback(this.instance.val());
      }
      this.hasSelected = false;
    },
    _onCursorRemoved() {
      this.instance.parent().find('.autocomplete-dropdown-suggestion.cursor').removeClass('cursor');
      this.instance.data().aaAutocomplete.dropdown.cursorTopSuggestion();
    },
    _onKeyDown(event) {
      const ARROW_UP_KEY = 38;
      const ARROW_DOWN_KEY = 40;

      if (event.keyCode === ARROW_UP_KEY ||
        event.keyCode === ARROW_DOWN_KEY) {
        this._scrollToFocusedSuggestion();
      }
    },
    _getData(query, callback) {
      if (this._shouldSearch(query)) {
        this.getData(query).then(list => {
          const foundList = searchData.search({
            query,
            list,
            searchFilter: this.searchFilter
          });
          callback(foundList);
        });
      }
    },
    getSuggestionTemplate(suggestion) {
      return createSuggestionTemplate.create(this.suggestionComponent, suggestion);
    },
    getHeaderTemplate({ isEmpty }) {
      let tmpl = '';
      if (this._hasAddNewItemBtn() && this.addButtonTop) {
        tmpl = `<div class="add-button bg-${this.addButtonColorValue}">${this.addNewItemLabel}</div>`;
      }
      if (!isEmpty && this.suggestionsHeaderComponent) {
        tmpl += createComponentTemplate.create(this.suggestionsHeaderComponent);
      }

      if (tmpl.length) {
        return tmpl;
      }
      return null;
    },
    getFooterTemplate({ isEmpty }) {
      let tmpl = '';
      if (!isEmpty && this.suggestionsFooterComponent) {
        tmpl += createComponentTemplate.create(this.suggestionsFooterComponent);
      }
      if (this._hasAddNewItemBtn() && !this.addButtonTop) {
        tmpl += `<div class="add-button bg-${this.addButtonColorValue}">${this.addNewItemLabel}</div>`;
      }

      if (tmpl.length) {
        return tmpl;
      }
      return null;
    },
    getEmptySuggestionsTemplate() {
      if (this.emptyResultMessage) {
        return `<div class="margin-left">${this.emptyResultMessage}</div>`;
      }
      return null;
    },
    syncAutocompleteValWithInputVal(value) {
      value = _getValue(value, this.display);
      this.instance.val(value);
      this.instance.autocomplete.setVal(value);
      this.modelValue = value;
    },
    _hasAddNewItemBtn() {
      return Boolean(this.addNewItemLabel && this.addNewItemCallback)
    },
    _onAddButtonClick() {
      this.instance.autocomplete.close();
      if (this.addNewItemCallback) {
        this.addNewItemCallback(this.instance.val());
      }
    },
    _scrollToFocusedSuggestion() {
      if (this.instance.data().aaAutocomplete.dropdown.isOpen) {
        const $container = this.instance[0].parentElement;
        const $parentDiv = jQuery($container.querySelector('.suggestions'));
        const $innerListItem = jQuery($container.querySelector('.autocomplete-dropdown-suggestion.cursor'));
        if ($container && $parentDiv && $innerListItem && $innerListItem.position()) {
          $parentDiv.scrollTop($parentDiv.scrollTop() + $innerListItem.position().top
            - $parentDiv.height() / 2 + $innerListItem.height() / 2);
        }
      }
    },
    _shouldSearch(query) {
      const qtdQueryChars = query && query.length;

      return Boolean(!this.minimumCharsToSearch ||
        qtdQueryChars >= this.minimumCharsToSearch);
    }
  }
};

const _getValue = (value, display) => {
  if (typeof value === 'object' && value !== null && display) {
    const objectValue = value[display];
    value = objectValue || value;
  }
  return value;
};

const createInstance = function() {
  return autocomplete(this.$el.querySelector('input'), buildInstanceOptions.buildOptions(this), buildInstanceOptions.buildSuggestionOptions(this));
};

export default component;
</script>
